/**
 * How a site and a timezone are written on screen.
 *
 * Both live here rather than in the components that draw them, because both are rendered in
 * five or six places and a label that differs between two pickers reads as two different
 * lists of things.
 */

/**
 * A site as people say it: `(PLR) Palmer Lake Recovery`.
 *
 * ⚠️ The code leads, and that is not decoration. The code is the stable handle — it is the
 * OU matching rule directory sync places people by (D-40), it is what reports are written
 * against, and it is what staff actually say to each other. Several sites have similar
 * names; no two share a code.
 *
 * The name follows because a code alone is unreadable to anyone who has not memorised
 * twenty-nine of them.
 */
export function formatSite(site: { code: string; name: string }): string {
  return `(${site.code}) ${site.name}`;
}

/**
 * US time zones as people name them, rather than as IANA writes them.
 *
 * ⚠️ "America/Los_Angeles" is an identifier, not a label. Nobody in this organisation calls
 * their timezone Los Angeles — they call it Pacific — and somebody scanning a list for the
 * zone they work in should not have to know which city stands in for their region.
 *
 * The city is kept alongside the region because the two together are unambiguous: Arizona
 * and Denver are both Mountain and only one of them observes daylight saving, so a list
 * showing "Mountain" twice would be a list you cannot choose from.
 */
const UsRegions: Record<string, string> = {
  "America/New_York": "Eastern",
  "America/Chicago": "Central",
  "America/Denver": "Mountain",
  "America/Phoenix": "Mountain (no DST)",
  "America/Los_Angeles": "Pacific",
  "America/Anchorage": "Alaska",
  "Pacific/Honolulu": "Hawaii",
};

/**
 * A timezone as a label: `Pacific — Los Angeles (PDT)`.
 *
 * The abbreviation is computed live, so it follows daylight saving rather than claiming EST
 * in July. A zone the browser does not recognise falls back to the city, which is still
 * better than the raw identifier.
 *
 * A zone outside the US keeps its city and abbreviation with no region prefix — inventing
 * one for somewhere we have no sites would be guessing in a way that looks authoritative.
 */
export function timeZoneLabel(zone: string): string {
  const city = zone.split("/").pop()?.replace(/_/g, " ") ?? zone;
  const region = UsRegions[zone];

  let abbreviation: string | undefined;

  try {
    abbreviation = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      timeZoneName: "short",
    })
      .formatToParts(new Date())
      .find((p) => p.type === "timeZoneName")?.value;
  } catch {
    // A zone this browser does not know. The city alone still beats the raw id.
    abbreviation = undefined;
  }

  const place = abbreviation ? `${city} (${abbreviation})` : city;

  return region ? `${region} — ${place}` : place;
}
